import React, { useState } from "react";
import { Box, Typography, Collapse, IconButton, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ChatSessionSummary } from "@/taskpane/services/chat";

// --- Types ---
interface ChatHistoryItemProps {
  session: ChatSessionSummary;
  onSessionClick: (sessionId: string) => void;
  onDeleteSession?: (sessionId: string) => Promise<void>;
  isDeleting?: boolean;
}

// --- Styled Components ---
const ItemContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  borderBottom: "1px solid #E4E7EC",
  "&:last-child": {
    borderBottom: "none",
  },
}));

const QuestionRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  padding: theme.spacing(1.5),
  cursor: "pointer",
  transition: theme.transitions.create(["background-color"], {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    backgroundColor: "#F9FAFB",
  },
}));

const ExpandIconButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  marginRight: theme.spacing(1),
  color: "#6B7280",
  "&:hover": {
    backgroundColor: "transparent",
    color: theme.palette.primary.main,
  },
}));

const QuestionContent = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  marginLeft: theme.spacing(1),
  color: "#9CA3AF",
  opacity: 0,
  transition: theme.transitions.create(["opacity", "color"], {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    backgroundColor: "#FEE2E2",
    color: "#DC2626",
  },
  ".MuiBox-root:hover &": {
    opacity: 1,
  },
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontFamily: "Inter, sans-serif",
  fontWeight: 500,
  color: "#111827",
  lineHeight: 1.4,
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
}));

const TimestampText = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  fontFamily: "Inter, sans-serif",
  fontWeight: 400,
  color: "#9CA3AF",
  marginTop: theme.spacing(0.5),
}));

const AnswerContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  paddingTop: 0,
  paddingLeft: theme.spacing(5),
}));

const AnswerText = styled(Typography)(({ theme }) => ({
  fontSize: "13px",
  fontFamily: "Inter, sans-serif",
  fontWeight: 400,
  color: "#4B5563",
  lineHeight: 1.5,
  backgroundColor: "#F9FAFB",
  padding: theme.spacing(1.5),
  borderRadius: "8px",
  borderLeft: `3px solid ${theme.palette.primary.main}`,
}));

// --- Utility Functions ---

/**
 * Format a timestamp into a relative time string in French
 */
const formatRelativeTime = (dateString: string): string => {
  if (!dateString || typeof dateString !== "string") {
    return "Date inconnue";
  }
  const date = new Date(dateString);
  const ts = date.getTime();
  if (Number.isNaN(ts)) {
    return "Date inconnue";
  }
  const now = new Date();
  const diffMs = now.getTime() - ts;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) {
    return "À l'instant";
  } else if (diffMinutes < 60) {
    return `Il y a ${diffMinutes} min`;
  } else if (diffHours < 24) {
    return `Il y a ${diffHours}h`;
  } else if (diffDays === 1) {
    return "Hier";
  } else if (diffDays < 7) {
    return `Il y a ${diffDays} jours`;
  } else {
    // Format as date for older items
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
    });
  }
};

// --- Component ---
export const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({
  session,
  onSessionClick,
  onDeleteSession,
  isDeleting = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleQuestionClick = () => {
    onSessionClick(session.session_id);
  };

  const handleDeleteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDeleteSession && !isDeleting) {
      await onDeleteSession(session.session_id);
    }
  };

  const hasAnswer = session.first_answer && session.first_answer.length > 0;

  return (
    <ItemContainer>
      <QuestionRow onClick={handleQuestionClick}>
        {hasAnswer && (
          <ExpandIconButton
            size="small"
            onClick={handleExpandClick}
            aria-label={isExpanded ? "Masquer la réponse" : "Afficher la réponse"}
          >
            {isExpanded ? (
              <KeyboardArrowDownIcon sx={{ fontSize: "20px" }} />
            ) : (
              <KeyboardArrowRightIcon sx={{ fontSize: "20px" }} />
            )}
          </ExpandIconButton>
        )}
        {!hasAnswer && <Box sx={{ width: 28, marginRight: 1 }} />}
        <QuestionContent>
          <QuestionText>{session.first_question || "Question sans contenu"}</QuestionText>
          <TimestampText>
            {formatRelativeTime(session.created_on)}
            {session.message_count > 2 && ` · ${session.message_count} messages`}
          </TimestampText>
        </QuestionContent>
        {onDeleteSession && (
          <DeleteButton
            size="small"
            onClick={handleDeleteClick}
            disabled={isDeleting}
            aria-label="Supprimer la conversation"
          >
            {isDeleting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DeleteOutlineIcon sx={{ fontSize: "18px" }} />
            )}
          </DeleteButton>
        )}
      </QuestionRow>

      {hasAnswer && (
        <Collapse in={isExpanded}>
          <AnswerContainer>
            <AnswerText>{session.first_answer}</AnswerText>
          </AnswerContainer>
        </Collapse>
      )}
    </ItemContainer>
  );
};

export default ChatHistoryItem;
